import { useState, useEffect } from "react";
import { message, Card, Form, Select, Input, DatePicker, Row, Col, Button, Flex, Upload } from "antd";
import { dateFormat, menuLinks, notifyMessages, requiredMessages } from "../../utils/constants";
import { UploadOutlined } from "@ant-design/icons";
import { PageHeader } from "../../components/common/pageHeader";
import { Link, useNavigate } from "react-router";
import { SaveOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useCurrentUserContext } from "../../context/currentUserContext";
import dayjs from 'dayjs';
import { handleDateConvertForApis } from "../../utils/functions";
import DistinteDocumentServices from "../../services/distinteDocumentServices";
import { fetchSuppliersList } from "../../state/slices/suppliersListSlice";

const NewDistinteDocument = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [fieldsChanged, setFieldsChanged] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);
    const { currentUser } = useCurrentUserContext();
    const [uploadedFile, setUploadedFile] = useState(null);
    const [fileList, setFileList] = useState([]);
    const regionsList = useSelector(state => state.regions.regions);
    const documentTypesDistinteList = useSelector(state => state.docTypes.docTypesDistinte);
    const suppliersList = useSelector(state => state.suppliers.suppliers);

    useEffect(() => {
        if (!currentUser) return;
        dispatch(fetchSuppliersList(currentUser.lastSelectedCategory, false, currentUser.id));
    }, [dispatch, currentUser]);

    const saveData = async (values) => {
        const formData = new FormData();
        values["LastEditedUser"] = currentUser.id;
        values.DateAdded = handleDateConvertForApis(values.DateAdded);

        const firstDay = values.Month.startOf('month');
        values.Month = handleDateConvertForApis(firstDay);
        formData.append("Document_Distinte_Model", JSON.stringify(values));

        if (uploadedFile) {
            formData.append("Attachment", uploadedFile);
        }
        setLoadingButton(true);
        try {

            const respData = await DistinteDocumentServices.addDocument(formData);

            if (respData.success) {
                message.success(notifyMessages.operationSuccess);
                navigate(menuLinks.distinte);
            } else {
                message.error(respData.ErrorDetail?.ErrorMessage || notifyMessages.errorAccured);
            }
        } catch (error) {
            message.error(notifyMessages.errorAccured);
        }
    };

    const beforeUpload = (file) => {
        setUploadedFile(file);
        setFileList([file]);
        return false;
    }

    const onRemove = () => {
        setUploadedFile(null);
        setFileList([]);
    };

    const handleFileChange = (info) => {
        if (info.file.status === "removed") {
            setUploadedFile(null);
            setFileList([]);
        } else {
            setUploadedFile(info.file);
            setFileList([...info.fileList]);
            if (info.file.status === "done") {
                message.success(`${info.file.name} file uploaded successfully`);
            } else if (info.file.status === "error") {
                message.error(`${info.file.name} file upload failed.`);
            }
        }
    };

    return (
        <div className="add-document">
            <PageHeader title={"Carica nuovo documento"} />
            <>
                <Card title="Dati documento" size="small" >
                    <Form
                        id="addDocument"
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 8 }}
                        onFinish={(values) => { saveData(values) }}
                        onValuesChange={() => setFieldsChanged(true)}
                    >
                        <Form.Item
                            label="Mese"
                            name="Month"
                            rules={[{ required: true, message: requiredMessages.requiredField }]}
                        >
                            <DatePicker placeholder="Selezione mese" picker="month" format={'MM/YYYY'} />
                        </Form.Item>

                        <Form.Item
                            label="CNA Provinciale"
                            name="RegionId"
                            rules={[{ required: true, message: requiredMessages.requiredField }]}
                        >
                            <Select
                                showSearch
                                optionFilterProp="label"
                                options={regionsList}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Manutentore"
                            name="SupplierId"
                            rules={[{ required: true, message: requiredMessages.requiredField }]}>

                            <Select
                                placeholder="Manutentore"
                                showSearch
                                optionFilterProp="label"
                                options={suppliersList}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Tipo"
                            name="DocumentTypeDistinte"
                            rules={[{ required: true, message: requiredMessages.requiredField }]}
                        >
                            <Select
                                showSearch
                                optionFilterProp="label"
                                options={documentTypesDistinteList}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Batch"
                            name="Flux"
                        >
                            <Input />
                        </Form.Item>

                        <Form.Item
                            label="Data caricamento"
                            name="DateAdded"
                            rules={[{ required: true, message: requiredMessages.requiredField }]}
                            initialValue={dayjs()}
                        >
                            <DatePicker format={dateFormat} />
                        </Form.Item>

                        <Form.Item
                            label="Note"
                            name="Notes"
                        >
                            <Input.TextArea rows={3} />
                        </Form.Item>

                        <Form.Item
                            label="Carica pdf"
                            name="pdf"
                            rules={[{ required: true, message: requiredMessages.requiredField }]}
                            valuePropName="fileList"
                            getValueFromEvent={(e) => e && e.fileList}
                        >
                            <Upload
                                maxCount={1}
                                accept='.pdf'
                                beforeUpload={beforeUpload}
                                fileList={fileList}
                                onChange={handleFileChange}
                                onRemove={onRemove}
                            >
                                <Button icon={<UploadOutlined />}>Seleziona PDF</Button>
                            </Upload>
                        </Form.Item>
                    </Form>
                </Card>

                <Row gutter={24}>
                    <Col span={24} className="modifyDocButtons">
                        <Flex gap="small" wrap>
                            <Button type="default">
                                <Link to={menuLinks.distinte}>Annulla</Link>
                            </Button>

                            <Button
                                loading={loadingButton}
                                type="primary"
                                htmlType="submit"
                                disabled={!fieldsChanged}
                                form="addDocument"
                            >
                                <SaveOutlined /> Salva
                            </Button>
                        </Flex>
                    </Col>
                </Row>
            </>
        </div>
    );
};

export default NewDistinteDocument;
